import { SDK_CHANNEL, EXTERNAL_BRIDGE_PROTOCOL, EXTERNAL_PORT_OFFER, EXTERNAL_PORT_ACK, parseGameMessage, } from './bridge-protocol.js';
export { EXTERNAL_BRIDGE_PROTOCOL, EXTERNAL_PORT_OFFER, EXTERNAL_PORT_ACK, } from './bridge-protocol.js';
/** Non-wire association: credentials remain plain protocol data; ports never do. */
const bootstrapPorts = new WeakMap();
export function attachBootstrapPort(bootstrap, port) {
    bootstrapPorts.set(bootstrap, port);
}
export function bootstrapPort(bootstrap) {
    return bootstrapPorts.get(bootstrap);
}
/** Call only for the launched iframe's FIRST load. The caller MUST close this
 * instance on every subsequent load/navigation and capture it in async replies.
 * Window transport carries only an offer and a transferred port, never tokens.
 * The port belongs to the receiving document, unlike a navigable WindowProxy. */
export function createExternalShellBridge(options) {
    if (new URL(options.expectedOrigin).origin !== options.expectedOrigin ||
        !options.expectedOrigin.startsWith('https://')) {
        throw new Error('External bridge requires an exact HTTPS origin');
    }
    const { port1, port2 } = options.createChannel?.() ?? new MessageChannel();
    let phase = 'OFFERED';
    const timer = setTimeout(() => {
        fail('EXTERNAL_BRIDGE_TIMEOUT');
    }, options.timeoutMs ?? 10_000);
    function close() {
        if (phase === 'CLOSED')
            return;
        phase = 'CLOSED';
        clearTimeout(timer);
        port1.onmessage = null;
        port1.onmessageerror = null;
        port1.close();
        port2.close();
    }
    function fail(reason) {
        if (phase === 'CLOSED')
            return;
        close();
        options.onFailure(reason);
    }
    port1.onmessageerror = () => {
        fail('EXTERNAL_BRIDGE_MESSAGE_ERROR');
    };
    function ready() {
        return phase === 'READY';
    }
    port1.onmessage = (event) => {
        if (phase === 'CLOSED')
            return;
        const data = event.data;
        if (phase === 'OFFERED') {
            if (data?.['channel'] !== SDK_CHANNEL ||
                data['type'] !== EXTERNAL_PORT_ACK ||
                data['protocol'] !== EXTERNAL_BRIDGE_PROTOCOL)
                return;
            phase = 'BOOTSTRAPPED';
            port1.postMessage({ channel: SDK_CHANNEL, type: 'BOOTSTRAP', bootstrap: options.bootstrap });
            return;
        }
        const message = parseGameMessage(event.data);
        if (message === undefined)
            return;
        if (phase === 'BOOTSTRAPPED') {
            if (message.type !== 'READY')
                return;
            phase = 'READY';
            clearTimeout(timer);
            options.onConnected();
        }
        if (ready())
            options.onMessage(message);
    };
    port1.start();
    try {
        options.frame.postMessage({ channel: SDK_CHANNEL, type: EXTERNAL_PORT_OFFER, protocol: EXTERNAL_BRIDGE_PROTOCOL }, options.expectedOrigin, [port2]);
    }
    catch {
        fail('EXTERNAL_BRIDGE_TRANSFER_FAILED');
    }
    return {
        send(message) {
            if (phase !== 'READY' || message.type === 'BOOTSTRAP')
                return false;
            try {
                port1.postMessage(message);
                return true;
            }
            catch {
                fail('EXTERNAL_BRIDGE_SEND_FAILED');
                return false;
            }
        },
        close,
    };
}
